import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { AlertTriangle, Clock, MapPin, Navigation } from "lucide-react"
import { MapRef, Marker } from "react-map-gl/mapbox"

import { DetailsPanelHeader } from "@/components/collections/monitoring/DetailsPanel"
import { MAPBOX_TOKEN, MapShell, MapUnavailable, RouteLine } from "@/components/shared/MapShell"
import { OVERLAY_SCRIM, PANEL_WIDTH } from "@/components/ui/overlay"
import {
  boundsOf,
  edgePadding,
  type LngLat,
  overlappingStopIds,
  segmentLines,
} from "@/lib/mapGeometry"
import { cn } from "@/lib/utils"
import { getSlotTypePinClasses } from "@/lib/slotTypeDisplay"
import { PlannedRoute, PlannedStop } from "@/types/collections"

export type { SegmentLineStyle } from "@/lib/routeCostCalculation"
import type { SegmentLineStyle } from "@/lib/routeCostCalculation"

export type MapRouteDisplay = Pick<PlannedRoute, "code" | "totalStops"> & {
  stops: Array<
    Pick<
      PlannedStop,
      "id" | "lat" | "lng" | "sequence" | "name" | "address" | "plannedArrival" | "plannedDeparture"
    > & { slotType?: string }
  >
}

const MIN_BOUNDS_DEGREES = 0.008 // ~800m
const BOUNDS_PADDING_PX = 80

interface PlanningRouteMapPanelProps {
  route: MapRouteDisplay | null
  segmentStyles?: SegmentLineStyle[]
  onClose: () => void
  inline?: boolean
  embedded?: boolean
  driverLocation?: { lat: number; lng: number }
  driverName?: string
}

const PlanningRouteMapPanel: React.FC<PlanningRouteMapPanelProps> = ({
  route,
  segmentStyles = [],
  onClose,
  inline = false,
  embedded = false,
  driverLocation,
  driverName,
}) => {
  const mapRef = useRef<MapRef>(null)
  const [mapLoaded, setMapLoaded] = useState(false)

  const stopsWithCoords = useMemo(() => {
    if (!route?.stops) return []
    return route.stops
      .filter((s) => s.lat != null && s.lng != null)
      .sort((a, b) => a.sequence - b.sequence)
  }, [route?.stops])

  const routeLineData = useMemo(
    () => (stopsWithCoords.length < 2 ? null : segmentLines(stopsWithCoords, segmentStyles)),
    [stopsWithCoords, segmentStyles],
  )

  const overlappingStopsMap = useMemo(() => overlappingStopIds(stopsWithCoords), [stopsWithCoords])

  const isStopOverlapping = useCallback(
    (stopId: string) => overlappingStopsMap.has(stopId),
    [overlappingStopsMap],
  )
  const getOverlappingStops = useCallback(
    (stopId: string): MapRouteDisplay["stops"] => {
      const ids = overlappingStopsMap.get(stopId) || []
      return stopsWithCoords.filter((s) => ids.includes(s.id))
    },
    [overlappingStopsMap, stopsWithCoords],
  )

  useEffect(() => {
    if (!mapRef.current || !mapLoaded) return
    const points = stopsWithCoords.map((s): LngLat => [s.lng, s.lat])
    if (driverLocation) points.push([driverLocation.lng, driverLocation.lat])
    const bounds = boundsOf(points, MIN_BOUNDS_DEGREES)
    if (!bounds) return
    mapRef.current.fitBounds(bounds, { padding: edgePadding(BOUNDS_PADDING_PX), duration: 400 })
  }, [route?.code, mapLoaded, stopsWithCoords, driverLocation])

  if (!route) return null

  const panelContent = (
    <div
      className={
        embedded
          ? "w-full flex flex-col bg-white rounded-lg border border-palette-jungleGreen200 overflow-hidden"
          : inline
            ? "w-full h-full flex flex-col bg-white border-l border-palette-jungleGreen200 min-w-0"
            : `fixed top-0 right-0 bottom-0 ${PANEL_WIDTH.default} max-w-full bg-white border-l border-palette-jungleGreen200 shadow-xl z-50 flex flex-col`
      }
    >
      {!embedded && (
        <DetailsPanelHeader
          leading={<MapPin size={16} className="flex-shrink-0 text-amber-500" />}
          title={route.code}
          meta={
            <span className="flex-shrink-0 text-xs text-palette-jungleGreen500">
              {route.totalStops} stops
            </span>
          }
          closeLabel={`Close map for route ${route.code}`}
          onClose={onClose}
        />
      )}

      <div className={cn("flex flex-col", embedded ? "h-64" : "flex-1 min-h-0")}>
        {!MAPBOX_TOKEN ? (
          <MapUnavailable />
        ) : (
          <div className="flex-1 min-h-0 relative">
            <MapShell
              mapRef={mapRef}
              initialViewState={{
                longitude: -0.1276,
                latitude: 51.5074,
                zoom: 10,
              }}
              onLoad={() => setMapLoaded(true)}
            >
              {routeLineData && <RouteLine id="planning-route-line" data={routeLineData} />}
              {stopsWithCoords.map((stop) => {
                const overlapping = isStopOverlapping(stop.id)
                const overlappingStops = getOverlappingStops(stop.id)
                const allNumbers = overlapping
                  ? [
                      ...new Set([stop.sequence, ...overlappingStops.map((s) => s.sequence)]),
                    ].sort((a, b) => a - b)
                  : [stop.sequence]
                return (
                  <Marker key={stop.id} longitude={stop.lng} latitude={stop.lat} anchor="bottom">
                    <div className="relative group">
                      <div className="absolute -top-6 left-1/2 -translate-x-1/2 flex items-center gap-0.5">
                        {allNumbers.map((num) => (
                          <span
                            key={num}
                            className="text-[10px] font-bold text-palette-jungleGreen700 bg-white rounded px-1.5 py-0.5 shadow-sm border border-palette-jungleGreen200"
                          >
                            {num}
                          </span>
                        ))}
                      </div>
                      <MapPin
                        size={28}
                        className={cn(getSlotTypePinClasses(stop.slotType, overlapping))}
                      />
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block z-10 pointer-events-none">
                        <div className="bg-white rounded shadow-lg border border-palette-jungleGreen200 p-2 min-w-[140px] max-w-[260px] text-left">
                          <div className="text-xs font-semibold text-palette-jungleGreen900">
                            {stop.name}
                          </div>
                          <div className="text-[10px] text-palette-jungleGreen500 truncate">
                            {stop.address}
                          </div>
                          <div className="text-[10px] text-palette-jungleGreen500 mt-0.5 flex items-center gap-1">
                            <Clock size={10} />
                            {new Date(stop.plannedArrival).toLocaleTimeString("en-GB", {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}{" "}
                            –{" "}
                            {new Date(stop.plannedDeparture).toLocaleTimeString("en-GB", {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </div>
                          {overlapping && overlappingStops.length > 0 && (
                            <div className="border-t border-palette-jungleGreen200 mt-2 pt-2">
                              <div className="text-[10px] text-palette-jungleGreen600 font-medium mb-1 flex items-center gap-1">
                                <AlertTriangle size={10} />
                                Multiple stops here:
                              </div>
                              {overlappingStops.map((s) => (
                                <div
                                  key={s.id}
                                  className="mt-1.5 pl-2 border-l-2 border-palette-jungleGreen200 text-[10px]"
                                >
                                  <span className="font-semibold text-palette-jungleGreen900">
                                    Stop {s.sequence}:
                                  </span>{" "}
                                  {s.name}
                                  <div className="text-palette-jungleGreen500 truncate">
                                    {s.address}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </Marker>
                )
              })}
              {driverLocation && (
                <Marker
                  longitude={driverLocation.lng}
                  latitude={driverLocation.lat}
                  anchor="center"
                  style={{ zIndex: 50 }}
                >
                  <div className="relative group z-[50]">
                    <div className="w-9 h-9 rounded-full shadow-lg flex items-center justify-center border-2 border-white bg-black">
                      <Navigation size={16} className="text-white" />
                    </div>
                    {driverName != null && driverName !== "" && (
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-[60] pointer-events-none">
                        <div className="bg-white rounded shadow-lg border border-palette-jungleGreen200 px-2.5 py-1.5 text-xs font-medium text-palette-jungleGreen900 whitespace-nowrap">
                          {driverName}
                        </div>
                      </div>
                    )}
                  </div>
                </Marker>
              )}
            </MapShell>
          </div>
        )}
      </div>
    </div>
  )

  if (inline) {
    return panelContent
  }
  return (
    <>
      <div className={cn("fixed inset-0 z-40", OVERLAY_SCRIM)} aria-hidden onClick={onClose} />
      {panelContent}
    </>
  )
}

export default PlanningRouteMapPanel
